import React from "react"
import {
  FieldErrors,
  Path,
  useFormContext,
  Controller,
} from "react-hook-form"
import { useTranslation } from "react-i18next"

import { getLocalizedText } from "utils/i18n/get-localized-text"
import { PlaceOrderFormData, Question } from "types"

interface CheckboxGroupQuestionProps {
  question: Question
  errors: FieldErrors<PlaceOrderFormData>
  ticketId: number
}

const CheckboxGroupQuestion: React.FC<CheckboxGroupQuestionProps> = ({
  question,
  errors,
  ticketId,
}) => {
  const { t } = useTranslation()
  const { control } = useFormContext<PlaceOrderFormData>()
  const fieldName =
    `tickets.${ticketId}.${question.uuid}` as Path<PlaceOrderFormData>

  const questionLabel = getLocalizedText(question.text, "Question")

  if (!question.choices || question.choices.length === 0) {
    return (
      <div className="text-red-500">
        {t("validation.no_choices_available", "Error: No choices available for this question")}
      </div>
    )
  }

  const sortedChoices = [...question.choices].sort((a, b) => a.rank - b.rank)

  // Validation logic for multiple-answer questions
  const getValidationRules = () => {
    return {
      validate: (value: any) => {
        const selectedValues = Array.isArray(value)
          ? value
          : value
          ? [String(value)]
          : []
        const selectedCount = selectedValues.length

        // If question is required and nothing is selected
        if (question.requires_answer && selectedCount === 0) {
          return t("validation.field_required", "This field is required")
        }

        // If user has started selecting options, validate the count constraints
        // This applies whether the question is required or not
        if (question.num_required_answers && selectedCount > 0) {
          if (question.require_exact_number_of_answers) {
            if (selectedCount !== question.num_required_answers) {
              return t(
                "validation.select_exactly",
                `Please select exactly ${question.num_required_answers} option${question.num_required_answers > 1 ? "s" : ""}`,
                { count: question.num_required_answers }
              )
            }
          } else {
            if (selectedCount < question.num_required_answers) {
              return t(
                "validation.select_at_least",
                `Please select at least ${question.num_required_answers} option${question.num_required_answers > 1 ? "s" : ""}`,
                { count: question.num_required_answers }
              )
            }
          }
        }

        return true
      },
    }
  }

  // Render checkboxes for multiple-answer questions using Controller
  const renderCheckboxes = () => {
    return (
      <Controller
        name={fieldName}
        control={control}
        rules={getValidationRules()}
        defaultValue={[]}
        render={({ field: { value, onChange } }) => {
          const selectedIds: number[] = Array.isArray(value) ? value : []

          const handleCheckboxChange = (
            choiceId: number,
            isChecked: boolean
          ) => {
            if (isChecked) {
              onChange([...selectedIds, choiceId])
            } else {
              onChange(selectedIds.filter((id) => id !== choiceId))
            }
          }

          return (
            <div className="space-y-3">
              {sortedChoices.map((choice) => {
                const isChecked = selectedIds.includes(choice.id)

                return (
                  <label
                    key={choice.id}
                    className="flex relative items-center cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={isChecked}
                      onChange={(e) =>
                        handleCheckboxChange(choice.id, e.target.checked)
                      }
                    />
                    <div className="flex relative items-center">
                      <div
                        className={`w-5 h-5 rounded border-2 ${
                          isChecked
                            ? "border-theme_accent bg-theme_accent"
                            : "bg-white border-input_border"
                        } flex items-center justify-center`}
                      >
                        {isChecked && (
                          <svg
                            className="w-3 h-3 text-white"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </div>
                    </div>
                    <span className="ml-3 text-sm text-theme_text">
                      {getLocalizedText(choice.text, "Choice")}
                    </span>
                  </label>
                )
              })}
            </div>
          )
        }}
      />
    )
  }

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-theme_text">
        {questionLabel}
        {question.requires_answer && (
          <span className="ml-1 text-red-500">*</span>
        )}
      </label>

      {renderCheckboxes()}

      {(() => {
        const ticketErrors = errors.tickets?.[ticketId]?.[question.uuid]
        return (
          ticketErrors && (
            <span className="text-xs text-red-500">{ticketErrors.message}</span>
          )
        )
      })()}
    </div>
  )
}

export { CheckboxGroupQuestion }
